/**
 * @fileoverview Standalone student Tasks Tracker page (AR-898):
 * `/app/student-task-tracker?project=<id>`. A project selector on top (the
 * student's assigned/finished projects) and the shared tracker content below.
 */

import { useCallback, useEffect, useMemo } from 'react'
import { Select, Stack } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { ArcPageShell } from 'components/ArcPageShell'
import { Layout } from 'components/Layout'
import { NotificationProvider } from 'context'
import { CurrentUserContextProvider } from 'context/useCurrentUser'
import { StudentTrackerContent, StudentTrackerEmptyState } from './components'
import { PARAM_PROJECT, PROJECT_SELECT_WIDTH } from './constants'
import { useStudentProjects } from './hooks/useStudentProjects'

function StudentTaskTrackerView() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { projects, loading: loadingProjects } = useStudentProjects()

  const projectId = searchParams.get(PARAM_PROJECT) ?? ''
  const selectedProject = useMemo(() => projects.find(project => project.id === projectId), [projects, projectId])

  const setProjectId = useCallback(
    (id: string | null) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          id ? next.set(PARAM_PROJECT, id) : next.delete(PARAM_PROJECT)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  // A student with a single project should not have to pick it.
  useEffect(() => {
    if (!projectId && !loadingProjects && projects.length === 1) setProjectId(projects[0].id)
  }, [projectId, loadingProjects, projects, setProjectId])

  const options = projects.map(project => ({ value: project.id, label: project.name }))

  return (
    <Layout>
      <ArcPageShell title={t('student_task_tracker.title')} subtitle={t('student_task_tracker.description')}>
        <Stack spacing='lg'>
          <Select
            label={t('student_task_tracker.project_label')}
            placeholder={t('student_task_tracker.select_project_placeholder')}
            data={options}
            value={projectId || null}
            onChange={setProjectId}
            searchable
            clearable
            nothingFound={t('student_task_tracker.no_project_match')}
            disabled={loadingProjects}
            style={{ width: PROJECT_SELECT_WIDTH, maxWidth: '100%' }}
          />

          {projectId ? (
            <StudentTrackerContent
              key={projectId}
              projectId={projectId}
              projectName={selectedProject?.name}
              projectStatus={selectedProject?.status}
              showProjectLink
            />
          ) : (
            <StudentTrackerEmptyState variant='select_project' />
          )}
        </Stack>
      </ArcPageShell>
    </Layout>
  )
}

export function StudentTaskTrackerPage() {
  return (
    <CurrentUserContextProvider>
      <NotificationProvider>
        <StudentTaskTrackerView />
      </NotificationProvider>
    </CurrentUserContextProvider>
  )
}
